/**
 * Hook para gerenciar credenciais seguras do tenant
 * Permite criar, listar, atualizar e deletar credenciais para webhooks/APIs
 * 
 * RELEASE: 1.0.0 - 2024-12-24
 */

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import type { 
  TenantCredential, 
  CreateCredentialInput, 
  UpdateCredentialInput,
  CredentialData 
} from '@/types/credentials';
import type { Json } from '@/integrations/supabase/types';
import type { Database } from '@/integrations/supabase/types';

const log = createLogger('useTenantCredentials');

/**
 * Hook principal para gerenciar credenciais do tenant
 * @param tenantId - ID do tenant atual
 */
export function useTenantCredentials(tenantId?: string) {
  const queryClient = useQueryClient();

  // Busca credenciais do tenant via RPC segura (SEM campo credentials/tokens)
  const { data: credentials, isLoading, error, refetch } = useQuery({
    queryKey: ['tenant-credentials', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      log.debug('Buscando credenciais do tenant via RPC segura:', tenantId);

      const { data, error } = await supabase.rpc('list_tenant_credentials_safe', {
        p_tenant_hint: tenantId,
      });

      if (error) {
        log.error('Erro ao buscar credenciais via RPC:', error);
        throw error;
      }

      log.debug(`${(data || []).length} credenciais encontradas (sem tokens expostos)`);
      
      // RPC retorna dados seguros — sem campo credentials
      return (data || []) as Omit<TenantCredential, 'credentials'>[];
    },
    enabled: !!tenantId,
  });

  // Credenciais ativas filtradas do resultado da RPC segura
  const activeCredentials = useMemo(
    () => (credentials || []).filter(c => c.is_active),
    [credentials]
  );

  // Criar nova credencial
  const createCredential = useMutation({
    mutationFn: async (input: CreateCredentialInput) => {
      if (!tenantId) {
        log.error('Tentativa de criar credencial sem tenant ID');
        throw new Error('Tenant ID não fornecido');
      }

      log.debug('Criando credencial:', { name: input.name, type: input.credential_type });

      const { data: user } = await supabase.auth.getUser();

      // Converte credentials para tipo compatível com Supabase (Json)
      const credentialsJson = JSON.parse(JSON.stringify(input.credentials)) as Json;

      const { data, error } = await supabase
        .from('tenant_credentials')
        .insert([{
          tenant_id: tenantId,
          name: input.name,
          credential_type: input.credential_type,
          credentials: credentialsJson,
          description: input.description,
          created_by: user.user?.id,
        }])
        .select()
        .single();

      if (error) {
        log.error('Erro ao criar credencial:', error);
        throw error;
      }

      log.info('Credencial criada com sucesso:', data.id);
      return {
        ...data,
        credentials: data.credentials as unknown as CredentialData
      } as TenantCredential;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-credentials', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-credentials-active', tenantId] });
      toast.success('Credencial criada com sucesso');
    },
    onError: (error: any) => {
      log.error('Erro na mutation de criar credencial:', error);
      
      if (error.code === '23505') {
        toast.error('Já existe uma credencial com esse nome');
      } else {
        toast.error('Erro ao criar credencial');
      }
    },
  });

  // Atualizar credencial existente
  const updateCredential = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateCredentialInput }) => {
      log.debug('Atualizando credencial:', { id, input: { ...input, credentials: '***' } });

      // Tipo seguro do Supabase para update de credenciais
      const updateData: Database['public']['Tables']['tenant_credentials']['Update'] = {};
      
      if (input.name !== undefined) updateData.name = input.name;
      if (input.credential_type !== undefined) updateData.credential_type = input.credential_type;
      if (input.credentials !== undefined) updateData.credentials = JSON.parse(JSON.stringify(input.credentials)) as Json;
      if (input.description !== undefined) updateData.description = input.description;
      if (input.is_active !== undefined) updateData.is_active = input.is_active;

      const { data, error } = await supabase
        .from('tenant_credentials')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        log.error('Erro ao atualizar credencial:', error);
        throw error;
      }

      log.info('Credencial atualizada com sucesso:', id);
      return {
        ...data,
        credentials: data.credentials as unknown as CredentialData
      } as TenantCredential;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-credentials', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-credentials-active', tenantId] });
      toast.success('Credencial atualizada com sucesso');
    },
    onError: (error: any) => {
      log.error('Erro na mutation de atualizar credencial:', error);
      
      if (error.code === '23505') {
        toast.error('Já existe uma credencial com esse nome');
      } else {
        toast.error('Erro ao atualizar credencial');
      }
    },
  });

  // Deletar credencial
  const deleteCredential = useMutation({
    mutationFn: async (id: string) => {
      log.debug('Deletando credencial:', id);

      const { error } = await supabase
        .from('tenant_credentials')
        .delete()
        .eq('id', id);

      if (error) {
        log.error('Erro ao deletar credencial:', error);
        throw error;
      }

      log.info('Credencial deletada com sucesso');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-credentials', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-credentials-active', tenantId] });
      toast.success('Credencial deletada com sucesso');
    },
    onError: () => {
      toast.error('Erro ao deletar credencial');
    },
  });

  return {
    // Dados
    credentials: credentials || [],
    activeCredentials: activeCredentials || [],
    isLoading,
    error,
    refetch,
    
    // Mutations
    createCredential: createCredential.mutate,
    createCredentialAsync: createCredential.mutateAsync,
    updateCredential: updateCredential.mutate,
    updateCredentialAsync: updateCredential.mutateAsync,
    deleteCredential: deleteCredential.mutate,
    
    // Estados de loading
    isCreating: createCredential.isPending,
    isUpdating: updateCredential.isPending,
    isDeleting: deleteCredential.isPending,
  };
}
